import { isLineChartInputData } from '@modules/charts/components/LineChartWrapper/helpers';
import { getDisplayNameForIncompleteTimeWindow } from '@modules/charts/components/LineChartWrapper/helpers/getDisplayNameForIncompleteTimeWindow';
import { LineChartTooltipPayload } from '@modules/charts/types';
import { getChartLabelIdOrName } from '@modules/charts/utils';
import { GetChartLabelOptions } from '@modules/common/types';
import { CircleRounded, DateRangeRounded } from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface LineChartTooltipProps {
  payload?: LineChartTooltipPayload[];
  active?: boolean;
  filter?: (payload: LineChartTooltipPayload[]) => LineChartTooltipPayload[];
  maxWidth?: string;
  getLabelOptions?: GetChartLabelOptions;
  formulaID: EQLFunctionName;
}

export const LineChartTooltip = ({
  payload,
  active = false,
  filter,
  maxWidth = '350px',
  getLabelOptions,
  formulaID,
}: LineChartTooltipProps) => {
  if (!active || !payload?.length) {
    return null;
  }

  const filteredPayload = filter?.(payload);
  const payloadToDisplay = filteredPayload?.length ? filteredPayload : payload;

  return (
    <Paper sx={{ borderRadius: 2, p: 1, maxWidth }} elevation={10}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
        spacing={1}
      >
        <DateRangeRounded
          fontSize="small"
          sx={{
            color: 'text.secondary',
          }}
        />
        <Typography variant="body2">
          {payloadToDisplay[0].payload.dateRange.replace('\0', ' - ')}
        </Typography>
      </Stack>
      <Stack spacing={0.5}>
        {payloadToDisplay
          .sort((a, b) => b.value - a.value)
          .map(({ color, name, value, payload }) => {
            const labelPayload = payload[name];
            const id = isLineChartInputData(labelPayload) && labelPayload.id;
            const labelIcon =
              getLabelOptions && id && getLabelOptions({ id, size: 12 })?.icon;

            return (
              <Stack
                key={name + value}
                spacing={1}
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                lineHeight="16px"
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <CircleRounded
                    sx={{ width: 12, height: 12, color, mt: 0.2 }}
                  />
                  <Stack direction="row" spacing={0.5} alignItems="flex-start">
                    <Box mt={-0.3}>{labelIcon}</Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      lineHeight="inherit"
                    >
                      {getChartLabelIdOrName(
                        getDisplayNameForIncompleteTimeWindow(name),
                      )}
                    </Typography>
                  </Stack>
                </Stack>
                <Typography variant="caption" lineHeight="inherit">
                  {value}
                  {formulaID === EQLFunctionName.NormaliseByPercent ? '%' : ''}
                </Typography>
              </Stack>
            );
          })}
      </Stack>
    </Paper>
  );
};
